import { QueueItem, QueueOperationType } from './offlineQueue';

// Conflict resolution rules interface
export interface ConflictRules {
  addOnly: boolean;
  preventClosedEdits: boolean;
  dedupeByKey: boolean;
  blockOfflineFinalize: boolean;
  preventDayCloseOffline: boolean;
}

// Operation validation result
export interface ValidationResult {
  isValid: boolean;
  error?: string;
  warnings?: string[];
}

// Conflict Resolution Service
export class ConflictResolutionService {
  private rules: ConflictRules = {
    addOnly: true,
    preventClosedEdits: true,
    dedupeByKey: true,
    blockOfflineFinalize: true,
    preventDayCloseOffline: true,
  };

  // Validate operation before queuing
  validateOperation(
    operationType: QueueOperationType,
    payload: any,
    isOnline: boolean = navigator.onLine
  ): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check if operation is an edit (not allowed in add-only mode)
    if (this.rules.addOnly && this.isEditOperation(payload)) {
      errors.push('Edit operations are not allowed in offline mode. Only add operations are permitted.');
    }

    // Check if operation is a finalize operation
    if (this.rules.blockOfflineFinalize && this.isFinalizeOperation(payload)) {
      if (!isOnline) {
        errors.push('Finalize operations are not allowed when offline.');
      } else {
        warnings.push('Finalize operations require online connectivity.');
      }
    }

    // Check if operation is a day close finalize
    if (this.rules.preventDayCloseOffline && this.isDayCloseFinalize(payload)) {
      if (!isOnline) {
        errors.push('Day close finalization is not allowed when offline.');
      } else {
        warnings.push('Day close finalization requires online connectivity.');
      }
    }

    // Check for closed item edits
    if (this.rules.preventClosedEdits && this.isClosedItemEdit(payload)) {
      errors.push('Cannot edit closed items.');
    }

    // Validate payload structure
    const payloadValidation = this.validatePayloadStructure(operationType, payload);
    if (!payloadValidation.isValid) {
      errors.push(payloadValidation.error!);
    }

    return {
      isValid: errors.length === 0,
      error: errors.length > 0 ? errors.join('; ') : undefined,
      warnings: warnings.length > 0 ? warnings : undefined,
    };
  }

  // Validate queue item before processing
  validateQueueItem(item: QueueItem, isOnline: boolean = navigator.onLine): ValidationResult {
    return this.validateOperation(item.operationType, item.payload, isOnline);
  }

  // Check if operation is an edit
  private isEditOperation(payload: any): boolean {
    const method = payload.method?.toUpperCase();
    const url = payload.url || '';
    
    // PUT and PATCH are typically edits
    if (method === 'PUT' || method === 'PATCH') {
      return true;
    }
    
    // POST to update endpoints
    if (method === 'POST' && (
      url.includes('/update') ||
      url.includes('/edit') ||
      url.includes('/modify')
    )) {
      return true;
    }
    
    return false;
  }

  // Check if operation is a finalize operation
  private isFinalizeOperation(payload: any): boolean {
    const url = payload.url || '';
    const method = payload.method?.toUpperCase();
    
    return (
      url.includes('/finalize') ||
      url.includes('/complete') ||
      url.includes('/close') ||
      (method === 'POST' && url.includes('/confirm'))
    );
  }

  // Check if operation is a day close finalize
  private isDayCloseFinalize(payload: any): boolean {
    const url = payload.url || '';
    
    return (
      url.includes('/daily-close') ||
      url.includes('/day-close') ||
      url.includes('/end-of-day')
    );
  }

  // Check if operation is editing a closed item
  private isClosedItemEdit(payload: any): boolean {
    // This would need to check the current state of the item
    // For now, we'll check for common patterns
    const data = payload.data || payload.body || {};
    
    // Check if the payload indicates a closed status
    if (data.status === 'closed' || data.status === 'completed') {
      return true;
    }
    
    // Check if the operation is trying to modify a closed item
    if (data.agreement_status === 'closed' || data.payment_status === 'completed') {
      return true;
    }
    
    return false;
  }

  // Validate payload structure
  private validatePayloadStructure(operationType: QueueOperationType, payload: any): ValidationResult {
    if (!payload) {
      return {
        isValid: false,
        error: 'Payload is required',
      };
    }

    if (!payload.url) {
      return {
        isValid: false,
        error: 'URL is required in payload',
      };
    }

    if (!payload.method) {
      return {
        isValid: false,
        error: 'HTTP method is required in payload',
      };
    }

    // Validate operation-specific requirements
    switch (operationType) {
      case 'create-agreement':
        return this.validateAgreementPayload(payload);
      case 'post-payment':
        return this.validatePaymentPayload(payload);
      case 'add-note':
        return this.validateNotePayload(payload);
      default:
        return { isValid: true };
    }
  }

  // Validate agreement payload
  private validateAgreementPayload(payload: any): ValidationResult {
    const data = payload.data || payload.body;
    
    if (!data) {
      return {
        isValid: false,
        error: 'Agreement data is required',
      };
    }

    const requiredFields = ['customer_id', 'branch_id', 'item_desc', 'total_price'];
    const missingFields = requiredFields.filter(field => !data[field]);
    
    if (missingFields.length > 0) {
      return {
        isValid: false,
        error: `Missing required fields: ${missingFields.join(', ')}`,
      };
    }

    return { isValid: true };
  }

  // Validate payment payload
  private validatePaymentPayload(payload: any): ValidationResult {
    const data = payload.data || payload.body;
    
    if (!data) {
      return {
        isValid: false,
        error: 'Payment data is required',
      };
    }

    const requiredFields = ['agreement_id', 'amount', 'payment_method'];
    const missingFields = requiredFields.filter(field => !data[field]);
    
    if (missingFields.length > 0) {
      return {
        isValid: false,
        error: `Missing required fields: ${missingFields.join(', ')}`,
      };
    }

    return { isValid: true };
  }

  // Validate note payload
  private validateNotePayload(payload: any): ValidationResult {
    const data = payload.data || payload.body;
    
    if (!data) {
      return {
        isValid: false,
        error: 'Note data is required',
      };
    }

    if (!data.note || data.note.trim().length === 0) {
      return {
        isValid: false,
        error: 'Note content is required',
      };
    }

    return { isValid: true };
  }

  // Update conflict rules
  updateRules(newRules: Partial<ConflictRules>): void {
    this.rules = { ...this.rules, ...newRules };
  }

  // Get current rules
  getRules(): ConflictRules {
    return { ...this.rules };
  }

  // Check if operation should be blocked
  shouldBlockOperation(
    operationType: QueueOperationType,
    payload: any,
    isOnline: boolean = navigator.onLine
  ): boolean {
    const validation = this.validateOperation(operationType, payload, isOnline);
    return !validation.isValid;
  }

  // Get blocking reason
  getBlockingReason(
    operationType: QueueOperationType,
    payload: any,
    isOnline: boolean = navigator.onLine
  ): string | null {
    const validation = this.validateOperation(operationType, payload, isOnline);
    return validation.error || null;
  }
}

// Export singleton instance
export const conflictResolution = new ConflictResolutionService(); 